// Trust configs: which SPIFFE trust domains and OIDC issuers the tenant trusts (docs/api.md 2.2).
import { useState, type FormEvent } from "react";
import type { Jwks, TenantDetail, TrustConfig, TrustConfigInput, TrustTestResult } from "../api/types";
import { Badge } from "../components/Badge";
import { useServices } from "../components/context";
import { IdCell } from "../components/IdCell";
import { PendingFilter, useGrace, type PendingRow } from "../components/Grace";
import { ErrorBanner, Section, useAction } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { formatTime } from "../lib/format";
import { TENANT_WIDE_HINT } from "../lib/namespaces";
import { atLeast } from "../lib/roles";

type JwksSource = "url" | "inline" | "discovery";

/** Builds a trust config body from the form. Returns an error message or the body. */
export function buildTrustConfig(f: {
  kind: "spiffe" | "oidc";
  name: string;
  trustDomain: string;
  issuer: string;
  source: JwksSource;
  jwksUrl: string;
  jwksInline: string;
  /** OIDC only. Empty means the default audience (ADR-0026). */
  audience?: string;
}): { error: string } | { body: TrustConfigInput } {
  const name = f.name.trim();
  if (!name) return { error: "Enter a name." };
  let jwks: { jwks_url?: string; jwks_inline?: Jwks } = {};
  if (f.source === "url") {
    if (!/^https:\/\/\S+$/.test(f.jwksUrl.trim())) return { error: "The JWKS URL must start with https://." };
    jwks = { jwks_url: f.jwksUrl.trim() };
  } else if (f.source === "inline") {
    let parsed: unknown;
    try {
      parsed = JSON.parse(f.jwksInline);
    } catch {
      return { error: "The JWKS is not valid JSON." };
    }
    const keys = (parsed as { keys?: unknown } | null)?.keys;
    if (!Array.isArray(keys) || keys.length === 0)
      return { error: 'The JWKS must have a non-empty "keys" array.' };
    jwks = { jwks_inline: parsed as Jwks };
  } else if (f.kind === "spiffe") {
    return { error: "A SPIFFE trust config needs a JWKS URL or an inline JWKS." };
  }
  if (f.kind === "spiffe") {
    const td = f.trustDomain.trim().toLowerCase();
    if (!/^[a-z0-9._-]+$/.test(td)) return { error: "Enter a trust domain, for example prod.acme.io." };
    return { body: { kind: "spiffe", name, spiffe_trust_domain: td, ...jwks } };
  }
  const issuer = f.issuer.trim();
  if (!/^https:\/\/\S+$/.test(issuer)) return { error: "The issuer must start with https://." };
  const audience = (f.audience ?? "").trim();
  if (audience.length > 255) return { error: "The audience can have at most 255 characters." };
  return { body: { kind: "oidc", name, issuer, ...jwks, ...(audience ? { audience } : {}) } };
}

export function TrustConfigs({ tenant }: { tenant: TenantDetail }) {
  const { api, config } = useServices();
  const list = useLoad(() => api.listTrustConfigs(tenant.slug), [tenant.slug]);
  const action = useAction();
  const isAdmin = atLeast(tenant.role, "admin");

  const [kind, setKind] = useState<"spiffe" | "oidc">("oidc");
  const [name, setName] = useState("");
  const [trustDomain, setTrustDomain] = useState("");
  const [issuer, setIssuer] = useState("");
  const [source, setSource] = useState<JwksSource>("discovery");
  const [jwksUrl, setJwksUrl] = useState("");
  const [jwksInline, setJwksInline] = useState("");
  const [audience, setAudience] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const [testId, setTestId] = useState<string | null>(null);
  const [testToken, setTestToken] = useState("");
  const [testResult, setTestResult] = useState<TrustTestResult | null>(null);
  const test = useAction();

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const r = buildTrustConfig({ kind, name, trustDomain, issuer, source, jwksUrl, jwksInline, audience });
    if ("error" in r) {
      setFormError(r.error);
      return;
    }
    setFormError(null);
    if (await action.run(async () => void (await api.createTrustConfig(tenant.slug, r.body)))) {
      setName("");
      setTrustDomain("");
      setIssuer("");
      setJwksUrl("");
      setJwksInline("");
      setAudience("");
      list.reload();
    }
  }

  const grace = useGrace<TrustConfig>({ tenant, collection: "trust-configs", onChange: list.reload });
  const pendingRows: PendingRow[] = grace.items.map((tc) => ({
    id: tc.id,
    title: (
      <>
        {tc.name} <Badge kind={tc.kind} />
      </>
    ),
    detail: (
      <>
        <span className="mono">{tc.spiffe_trust_domain ?? tc.issuer}</span>. Its principals come back with it.
      </>
    ),
    deletedBy: tc.deleted_by,
    deletedAt: tc.deleted_at,
    purgeAfter: tc.purge_after,
    purge: {
      id: tc.id,
      title: `trust config "${tc.name}"`,
      cascade: "Its principals and their grants are deleted too.",
      confirm: tc.name,
      confirmLabel: "trust config name",
    },
  }));

  // A trust config always needs the typed name (ADR-0031 section 5).
  function onDelete(id: string, label: string) {
    grace.askDelete({
      id,
      title: `trust config "${label}"`,
      message:
        "Prego stops accepting its tokens at once. Its principals stop working, and their registry tokens stop within about 60 seconds.",
      typedName: label,
      typedLabel: "trust config name",
      impact: "trust-configs",
    });
  }

  async function onTest(e: FormEvent) {
    e.preventDefault();
    if (!testId) return;
    setTestResult(null);
    const token = testToken.trim();
    await test.run(async () => {
      setTestResult(await api.testTrustConfig(tenant.slug, testId, token));
    });
    // Do not keep the sample token in the page after the test.
    setTestToken("");
  }

  const items = list.data?.items ?? [];

  return (
    <>
      <Section
        title="Trust configs"
        actions={<PendingFilter view={grace.view} setView={grace.setView} count={grace.count} />}
      >
        <p className="muted">
          A trust config says which machine identity tokens Prego accepts for this tenant.
        </p>
        <p className="hint">{TENANT_WIDE_HINT}</p>
        <ErrorBanner error={list.error ?? action.error ?? grace.error} />
        {grace.view === "pending" ? (
          grace.rowsTable(pendingRows)
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Trust domain or issuer</th>
                <th>JWKS</th>
                <th>Audience</th>
                <th>Created</th>
                <th>ID</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((tc) => (
                <tr key={tc.id}>
                  <td>{tc.name}</td>
                  <td>
                    <Badge kind={tc.kind} />
                  </td>
                  <td className="mono">{tc.spiffe_trust_domain ?? tc.issuer}</td>
                  <td className="mono">{tc.jwks_url ?? (tc.jwks_inline ? "inline" : "discovery")}</td>
                  <td className="mono">
                    {tc.kind === "oidc" ? (tc.audience ?? `${config.subjectAudience} (default)`) : ""}
                  </td>
                  <td>{formatTime(tc.created_at)}</td>
                  <IdCell id={tc.id} />
                  <td className="actions" data-label="Actions">
                    {isAdmin && (
                      <>
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => {
                            setTestId(tc.id);
                            setTestResult(null);
                          }}
                        >
                          Test a token
                        </button>
                        <button
                          type="button"
                          className="danger"
                          disabled={action.busy || grace.busy}
                          onClick={() => onDelete(tc.id, tc.name)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {grace.ui}

      {isAdmin && testId && (
        <Section title={`Test a token: ${items.find((t) => t.id === testId)?.name ?? testId}`}>
          <form onSubmit={onTest} className="form">
            <label>
              Subject token (JWT)
              <textarea
                required
                rows={4}
                spellCheck={false}
                autoComplete="off"
                value={testToken}
                onChange={(e) => setTestToken(e.target.value)}
                placeholder="eyJ..."
              />
              <small className="muted">
                Prego checks the token and shows which principal matches. It does not store the token.
              </small>
            </label>
            <div className="row">
              <button type="submit" disabled={test.busy}>
                Test
              </button>
              <button type="button" className="link" onClick={() => setTestId(null)}>
                Close
              </button>
            </div>
          </form>
          <ErrorBanner error={test.error} />
          {testResult && (
            <p className={`banner ${testResult.ok ? "ok" : "error"}`} role="status">
              {testResult.ok ? `Match: principal ${testResult.principal_id}. ` : "No match. "}
              {testResult.reason}
            </p>
          )}
        </Section>
      )}

      {isAdmin && (
        <Section title="Add a trust config">
          <form onSubmit={onCreate} className="form">
            <p className="hint">{TENANT_WIDE_HINT}</p>
            <fieldset className="row">
              <legend>Kind</legend>
              <label className="check">
                <input type="radio" name="kind" checked={kind === "oidc"} onChange={() => setKind("oidc")} />{" "}
                OIDC issuer
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="kind"
                  checked={kind === "spiffe"}
                  onChange={() => {
                    setKind("spiffe");
                    if (source === "discovery") setSource("url");
                  }}
                />{" "}
                SPIFFE trust domain
              </label>
            </fieldset>
            <label>
              Name
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={kind === "oidc" ? "on-prem GitLab" : "prod SPIRE"}
              />
            </label>
            {kind === "spiffe" ? (
              <label>
                Trust domain
                <input
                  required
                  value={trustDomain}
                  onChange={(e) => setTrustDomain(e.target.value)}
                  placeholder="prod.acme.io"
                />
              </label>
            ) : (
              <label>
                Issuer
                <input
                  required
                  value={issuer}
                  onChange={(e) => setIssuer(e.target.value)}
                  placeholder="https://gitlab.acme.io"
                />
              </label>
            )}
            {kind === "oidc" && (
              <label>
                Audience (optional)
                <input
                  value={audience}
                  maxLength={255}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={config.subjectAudience}
                />
                <small className="muted">
                  Leave empty to use {config.subjectAudience}. Microsoft Entra ID needs the application ID URI
                  of your app registration, for example api://prego (docs/issuers.md).
                </small>
              </label>
            )}
            <fieldset className="row">
              <legend>Keys (JWKS)</legend>
              {kind === "oidc" && (
                <label className="check">
                  <input
                    type="radio"
                    name="src"
                    checked={source === "discovery"}
                    onChange={() => setSource("discovery")}
                  />{" "}
                  OIDC discovery
                </label>
              )}
              <label className="check">
                <input type="radio" name="src" checked={source === "url"} onChange={() => setSource("url")} />{" "}
                JWKS URL
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="src"
                  checked={source === "inline"}
                  onChange={() => setSource("inline")}
                />{" "}
                Paste JWKS
              </label>
            </fieldset>
            {source === "url" && (
              <label>
                JWKS URL
                <input
                  required
                  value={jwksUrl}
                  onChange={(e) => setJwksUrl(e.target.value)}
                  placeholder="https://spire-oidc.acme.io/keys"
                />
              </label>
            )}
            {source === "inline" && (
              <label>
                JWKS (JSON)
                <textarea
                  required
                  rows={6}
                  spellCheck={false}
                  value={jwksInline}
                  onChange={(e) => setJwksInline(e.target.value)}
                  placeholder='{"keys": [ ... ]}'
                />
                <small className="muted">
                  Use this when Prego cannot reach the issuer. Update it when the issuer rotates its keys.
                </small>
              </label>
            )}
            {formError && (
              <p className="banner error" role="alert">
                {formError}
              </p>
            )}
            <button type="submit" disabled={action.busy}>
              Add trust config
            </button>
          </form>
        </Section>
      )}
    </>
  );
}
